import { ChevronDown16Regular } from '@fluentui/react-icons'
import { cn } from '@opoint/infomedia-storybook'
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible'
import { forwardRef, LiHTMLAttributes } from 'react'
import { NavLink, useMatches, useParams } from 'react-router-dom'

import { useOpointUser } from '@opoint/authjs-react'
import { isNavigationItemChecked } from '../../../../helpers/navigation'
import useSearchRouteBuilder from '../../../hooks/useSearchRouteBuilder'
import { ProfileWithChildren } from '../../../types/profile'

import NavigationNewMentionsBadge from './NavigationNewMentionsBadge'

type NavigationProfileProps = {
  profile: ProfileWithChildren
  depth?: number
  selectedProfileIds: number[]
} & Omit<LiHTMLAttributes<HTMLLIElement>, 'id' | 'children'>

export const NavigationProfileSingle = forwardRef<HTMLLIElement, NavigationProfileProps>(
  ({ profile, selectedProfileIds, depth = 0, className, ...props }, ref) => {
    const { getProfileEditorRoute } = useSearchRouteBuilder()
    const user = useOpointUser()
    const matches = useMatches()

    const params = useParams()
    const profileId = Number(params.profileId)
    const isNewProfileEditor = matches.find(({ pathname }) => pathname === '/profiles')
    const isLegacyProfileEditor = matches.find(
      ({ pathname }) => pathname === '/search/edit/' || pathname === '/search/new',
    )
    const selectedItems = isNewProfileEditor ? [profileId] : selectedProfileIds

    const navigationChecked = isNavigationItemChecked(profile, selectedItems)
    const itemChecked = !!navigationChecked && navigationChecked !== 'indeterminate'
    const isIntermediate = navigationChecked === 'indeterminate'

    // In the profile builder navigation, disable the navigation link if the user is not the owner of the profile (do not allow profile editing)
    const isDisabled = (isNewProfileEditor || isLegacyProfileEditor) && profile.owner !== user?.user_id

    const itemHeader = (
      <NavLink
        to={getProfileEditorRoute(profile.id)}
        title={profile.name}
        className={cn('block flex-1 cursor-pointer truncate px-4 py-3 text-label-l text-grey.1 hover:text-grey.1', {
          'font-bold text-white focus:text-white hover:text-white': itemChecked,
          'text-grey.2 pointer-events-none': isDisabled,
        })}
        style={{ paddingLeft: `${2.625 + depth * 1.25}rem` }} // 2.625rem for default spacing and 1.25rem for each level of depth
        data-cy="navigationProfileItem"
      >
        {profile.name}
      </NavLink>
    )

    if (!profile.children.length) {
      return (
        <li
          ref={ref}
          className={cn('mx-2.5 flex h-10 items-center gap-x-3', { 'bg-sky.1 rounded': itemChecked }, className)}
          {...props}
        >
          {itemHeader}
          <div className="flex size-10 items-center justify-center">
            <NavigationNewMentionsBadge profileId={profile.id} inverted={itemChecked} />
          </div>
        </li>
      )
    }

    return (
      <CollapsiblePrimitive.Root asChild defaultOpen={isIntermediate}>
        <li ref={ref} className={cn('flex flex-col', className)} {...props}>
          <span
            className={cn('mx-2.5 flex h-10 items-center gap-x-3', {
              'bg-sky.1 rounded': itemChecked,
            })}
          >
            {itemHeader}
            <div className="flex items-center">
              <NavigationNewMentionsBadge profileId={profile.id} inverted={itemChecked} />
              <CollapsiblePrimitive.Trigger
                disabled={isIntermediate}
                className={cn('group/trigger flex size-10 items-center justify-center rounded text-sky.1', {
                  'cursor-not-allowed': isIntermediate,
                })}
              >
                <ChevronDown16Regular
                  className={cn('group-data-[state=open]/trigger:rotate-180', {
                    'text-white': itemChecked,
                    'text-grey.5': isIntermediate,
                  })}
                />
              </CollapsiblePrimitive.Trigger>
            </div>
          </span>
          <CollapsiblePrimitive.Content asChild>
            <ul>
              {profile.children.map((profile) => (
                <NavigationProfileSingle
                  depth={depth + 1}
                  key={profile.id}
                  profile={profile}
                  selectedProfileIds={selectedProfileIds}
                />
              ))}
            </ul>
          </CollapsiblePrimitive.Content>
        </li>
      </CollapsiblePrimitive.Root>
    )
  },
)
NavigationProfileSingle.displayName = 'NavigationProfileSingle'
